import _ from 'lodash';

export interface IArgumentType {
    message: string;
    extraData?: object;
    cause?: Exception;
    name?: string;
}

/**
 *  The parent class for all exception types.
 *
 *  @param message - required - The detailed cause of the exception.
 *  @param cause - not required - The nested Exception that caused this exception.
 *                  All caught exceptions should be wrapped in the new
 *                  exception that is thrown.
 *  @param serverCause - optional
 *  @param reasonCode - optional
 *  @param flag - optional - use it for communicating any extra info into the Exception.
 */
export class Exception extends Error {
    private readonly cause: Exception;

    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        // Error breaks the prototype chain by switching the object to be constructed (i.e. this)
        // to a new, different object, when we call super.
        // That new object doesn't have the expected prototype chain, i.e. it's an instance of Error not of Exception.
        super(args.message);

        // restore prototype chain
        Object.setPrototypeOf(this, new.target.prototype);

        if (args.cause instanceof Exception) {
            this.cause = args.cause;
        }
        if (typeof args.name === 'string') {
            this.name = args.name;
        }
    }

    public getMessage = (): string => {
        return this.message;
    };

    public getCause = (): Exception => {
        return this.cause;
    };

    /*
     * Returns a string representation of the top level exception and its nested exceptions.
     */
    public toString(): string {
        const exceptions = [];
        exceptions.push(this);

        let currentException = this.getCause();
        while (currentException) {
            exceptions.push(currentException);
            currentException = currentException.getCause();
        }
        let stackTrace;
        _.each(exceptions.reverse(), (exception) => {
            stackTrace = {
                message: exception.getMessage(),
                cause: stackTrace,
            };
        });
        return JSON.stringify(stackTrace, null, 4);
    }
}
